/**
 * Browser error reporting and Session Replay.
 *
 * Settings are read at page load, not baked in at build time: one image is
 * built per commit and promoted through every stage, so the DSN, environment
 * and sample rates come from window.__QPRS_CONFIG__, which the nginx container
 * writes into /env-config.js from its SENTRY_* env vars on startup (see
 * frontend/docker/env-config.sh). VITE_SENTRY_* is the fallback for `npm run
 * dev`. With no DSN anywhere, Sentry is never initialised - local dev and the
 * test suite send nothing.
 *
 * What is reported:
 *   - uncaught errors, unhandled promise rejections, React render crashes
 *   - every 4xx/5xx response from our backends (fetch and axios alike),
 *     except the expected 401/403 from the session check on a logged-out load
 *   - requests that never got a response at all - a dropped connection, a
 *     phone backgrounding the tab mid-upload. The caller catches these and
 *     shows a message, so without this they are invisible.
 *   - every console.error. Components catch errors and console.error them
 *     instead of rethrowing; that is where most handled failures surface.
 * Each error object is reported once, however many of these paths it takes.
 *
 * Replays mask all text and inputs and block media. These pages show exam
 * papers, reviews and reviewer names; a replay still shows what was clicked,
 * the page flow, console errors and failed requests, which is what a "the
 * upload just failed on my phone" report needs.
 */
import * as Sentry from '@sentry/react';
import axios from 'axios';

const toRate = (value, fallback) => {
  if (value === undefined || value === null || value === '') return fallback;
  const rate = Number(value);
  return Number.isFinite(rate) ? Math.min(1, Math.max(0, rate)) : fallback;
};

export function readSentryConfig(runtime = window.__QPRS_CONFIG__ || {}, build = import.meta.env) {
  const pick = (key) => runtime[`SENTRY_${key}`] ?? build[`VITE_SENTRY_${key}`];
  return {
    dsn: (pick('DSN') || '').trim(),
    environment: pick('ENVIRONMENT') || 'development',
    release: pick('RELEASE') || undefined,
    tracesSampleRate: toRate(pick('TRACES_SAMPLE_RATE'), 0.1),
    replaysSessionSampleRate: toRate(pick('REPLAYS_SESSION_SAMPLE_RATE'), 0.1),
    // Every session that hits an error keeps its replay - those are the ones
    // anyone will actually open.
    replaysOnErrorSampleRate: toRate(pick('REPLAYS_ON_ERROR_SAMPLE_RATE'), 1.0),
  };
}

// UUIDs and numeric ids collapse to :id so one failing endpoint groups into
// one issue rather than one per paper.
const ID_SEGMENT = /\/(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)(?=\/|$)/gi;

export function requestPath(url) {
  try {
    return new URL(String(url), window.location.origin).pathname.replace(ID_SEGMENT, '/:id');
  } catch {
    return 'unknown';
  }
}

// A logged-out visit to any protected page answers 401/403 here by design.
const EXPECTED_FAILURES = [{ path: '/qpra/verify-session', statuses: [401, 403] }];

export function isExpectedHttpFailure(event) {
  const status = event?.contexts?.response?.status_code;
  const url = event?.request?.url;
  if (!status || !url) return false;
  const path = requestPath(url);
  return EXPECTED_FAILURES.some((rule) => rule.path === path && rule.statuses.includes(status));
}

const reported = new WeakSet();

export function beforeSend(event, hint) {
  const error = hint?.originalException;
  if (error && typeof error === 'object') {
    if (reported.has(error)) return null;
    reported.add(error);
  }
  return isExpectedHttpFailure(event) ? null : event;
}

function reportNetworkFailure(error, method, url, client) {
  const path = requestPath(url);
  Sentry.captureException(error, {
    tags: {
      'http.failure': 'network',
      'http.method': method,
      'http.path': path,
      'http.client': client,
      online: typeof navigator === 'undefined' ? 'unknown' : String(navigator.onLine),
    },
    fingerprint: ['network-failure', method, path],
  });
}

export function instrumentNetworkFailures(win = window, axiosInstance = axios) {
  const originalFetch = win.fetch;
  if (typeof originalFetch === 'function') {
    win.fetch = async function fetchWithFailureReporting(input, init) {
      try {
        return await originalFetch.call(this, input, init);
      } catch (error) {
        // An AbortError is the page cancelling its own request, not a failure.
        if (error?.name !== 'AbortError') {
          const method = (init?.method || input?.method || 'GET').toUpperCase();
          reportNetworkFailure(error, method, input?.url ?? input, 'fetch');
        }
        throw error;
      }
    };
  }
  axiosInstance.interceptors.response.use(undefined, (error) => {
    // With a response, httpClientIntegration has already reported the status.
    if (!error?.response && !axios.isCancel(error)) {
      const method = (error?.config?.method || 'get').toUpperCase();
      reportNetworkFailure(error, method, error?.config?.url, 'axios');
    }
    return Promise.reject(error);
  });
}

export function initSentry(config = readSentryConfig()) {
  if (!config.dsn) return false;
  Sentry.init({
    dsn: config.dsn,
    environment: config.environment,
    release: config.release,
    sendDefaultPii: false,
    tracesSampleRate: config.tracesSampleRate,
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
    beforeSend,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
      }),
      Sentry.httpClientIntegration({ failedRequestStatusCodes: [[400, 599]] }),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),
    ],
  });
  // After init, so this wrapper sits outside Sentry's own fetch
  // instrumentation and sees the rejection the caller would see.
  instrumentNetworkFailures();
  return true;
}
